import { AcknowledgementType, Author, ContentItem, ExtendedContentItem, filterTypes, Meta, ObjectionableComment, ParentList, Workspace } from  "@/interfaces/activityFeed";
import { getOrdinalSuffix, getTimeAtStartOfDay } from "./Dates";
import { getContentUrl } from "./Content";
import { MediaType, IMediaItem } from "thrive-components";

export const formatDateGroup = (date: string): string => {
    const now = new Date();
    const activityDate = new Date(date);

    const dayDifference = Math.floor(
        (getTimeAtStartOfDay(now).getTime() - getTimeAtStartOfDay(activityDate).getTime()) / 
        (1000 * 60 * 60 * 24)
    );
    const isSameYear = now.getFullYear() === activityDate.getFullYear();
    const weekdayFormatter = new Intl.DateTimeFormat("en-US", { weekday: "long" });

    if (dayDifference === 0) return "Today";
    if (dayDifference === 1) return "Yesterday";
    if (dayDifference <= 5) return weekdayFormatter.format(activityDate);

    // Format the date with ordinal day
    const day = activityDate.getDate();
    const month = activityDate.toLocaleString("en-US", { month: "long" });
    const weekday = weekdayFormatter.format(activityDate);
    const year = activityDate.getFullYear();

    const ordinalDay = `${day}${getOrdinalSuffix(day)}`;
    
    return `${weekday}, ${ordinalDay} ${month}${isSameYear ? "" : ` ${year}`}`;
};

export const formatLongDateGroup = (date: string): string => {
    const activityDate = new Date(date);
    const weekdayFormatter = new Intl.DateTimeFormat("en-US", { weekday: "long" });

    const day = activityDate.getDate();
    const month = activityDate.toLocaleString("en-US", { month: "long" });
    const weekday = weekdayFormatter.format(activityDate);
    const year = activityDate.getFullYear();

    const ordinalDay = `${day}${getOrdinalSuffix(day)}`;
    
    return `${weekday}, ${ordinalDay} ${month} ${year}`;
};

export const formatActivityTime = (date: string): string => {
    //will display the time like so: "12:02"
    const activityDate = new Date(date);
    return new Intl.DateTimeFormat("en", { hour: "2-digit", minute: "2-digit", hour12: false }).format(activityDate);
};

export const formatActivityWithShortDate = (date: string): string => {
    const parts = new Intl.DateTimeFormat("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric"
    }).formatToParts(new Date(date));

    const get = (type: string) => parts.find(part => part.type === type)?.value ?? '';

    return `${get('day')}/${get('month')}/${get('year')}`;
}

export const formatActivityDateWithYear = (date: string): string => {
    //will display the time like so: "Saturday, 11 January 2025"
    const activityDate = new Date(date);
    const dateParts = new Intl.DateTimeFormat("en", { weekday: 'long', day:'2-digit', month:'long', year:'numeric' }).formatToParts(activityDate);
    const dateObj: any = {};

    for(const date of dateParts) {
        dateObj[date.type as string] = date.value
    }

    return `${dateObj.weekday}, ${dateObj.day} ${dateObj.month} ${dateObj.year}`


};

export function formatDateFilterContent(dates: Date[]): string {
    

    if(!dates.length) {
        return ''
    }

    if (dates.length === 1) {
        return formatActivityDateWithYear(dates[0].toString())
    }


    return `${formatActivityDateWithYear(dates[0].toString())} - ${formatActivityDateWithYear(dates[1].toString())}`
}

export function formatShortDateFilterContent(dates: Date[]): string {
    if(!dates.length) {
        return ''
    }

    if (dates.length === 1) {
        return formatActivityWithShortDate(dates[0].toString())
    }

    return `${formatActivityWithShortDate(dates[0].toString())} - ${formatActivityWithShortDate(dates[1].toString())}`
}


export const groupActivitiesByDate = (activities: any) => {
    const groupedActivities: { dateLabel: string; longDateLabel: string, groupedActivities: any[] }[] = [];
    let groupingDateLabel = "";

    activities.forEach((activity: any) => {
        const itemDateLabel : string = formatDateGroup(activity.createdAt);
        const itemLongDateLabel : string = formatLongDateGroup(activity.createdAt);
        // If the date is different/new, then we are gonna create a new group of activities with a new label
        //if the date is the same then we are just pushing activities into the existing group with the existing label
        if (itemDateLabel !== groupingDateLabel) {
            groupedActivities.push({ dateLabel: itemDateLabel, longDateLabel: itemLongDateLabel, groupedActivities: [] });
            groupingDateLabel = itemDateLabel;
        }
        groupedActivities[groupedActivities.length - 1].groupedActivities.push(activity);
    });

    return groupedActivities;
};

export function mapActivitiesToEnum(activityType: string): string[] {
    const acceptedTypes = filterTypes;

    if (acceptedTypes.includes(activityType)){
        const type =  {
            Comments: ['Comment', 'objectionableComment'],
            ['Comment Likes']: ['CommentLike'],
            ['Recognition Posts']: ['Recognition', 'moderatedRecognition'],
            Likes: ['ContentLike'],
            Posts: ['ContentPosted', 'moderatedContent'],
            Acknowledgements: ['AcknowledgementResponse'],
            ['Moderation Items'] : ['moderatedContent', 'moderatedRecognition', 'objectionableComment'],
            ['Form Responses'] : ['formResponse'],
            ['Passkey Recovery'] : ["accountRecoveryRequest"]
        }[activityType]
        
        return type ?? []
    }
    return []; 
}

export function getFeedFilters(selectedFilters: string[]): string[] {
    //will remove duplicate filters

    return Array.from(
        new Set(selectedFilters
            .flatMap(filter => mapActivitiesToEnum(filter))
            .filter(f => f.length > 0)
        )
    );
}

export function getUserDetails(author?: Author) {
    return {
        id: author?.id ?? 0,
        imageUrl: author?.profilePicture?.url ?? undefined,
        name: author?.displayName ?? "Unknown User",
    }
}

export function getObjectionableCommentDetails(objectionableComment: ObjectionableComment) {
    return {
        commentAuthor: {
            id: objectionableComment?.commentAuthor.id,
            imageUrl: objectionableComment.commentAuthor?.profilePicture?.url ?? undefined,
            name: objectionableComment.commentAuthor?.displayName ?? "Unknown User",            
        },
        commentId: objectionableComment.commentId,
        commentText: objectionableComment.commentText, 
        isDeleted: objectionableComment.isDeleted, 
        objectionableMessage: objectionableComment.objectionableMessage
    }
}


export function getContentDetails(content: ContentItem, parentList: ParentList | undefined, workspace: Workspace, existsInMultiplePlaces: boolean) {
    return { 
        id: content.id, 
        title: content.title,
        link: getContentUrl(
            workspace.appGuid,
            content.id.toString(),
            parentList?.id?.toString()
        ),
        isArchived: content?.isArchived,
        existsInMultiplePlaces: existsInMultiplePlaces
    }
}

export function getExtendedContentDetails(content: ExtendedContentItem, parentList: ParentList, workspace: Workspace, meta: Meta, existsInMultiplePlaces: boolean) {
    return { 
        id: content.id, 
        title: content.title,
        link: getContentUrl(
            workspace.appGuid,
            content.id.toString(),
            parentList?.id?.toString()
        ),
        isArchived: content?.isArchived,
        media: content.media?.total > 0 ? getMediaItems(content.media?.items) : [],
        totalMediaItems: content.media?.total,
        likes: meta.likeCount ?? undefined,
        comments: meta.commentCount ?? undefined,
        isLiked: meta.hasLiked ?? false,
        existsInMultiplePlaces: existsInMultiplePlaces
    }
}

export function getMediaItems(mediaItems: IMediaItem[]): IMediaItem[] {
    return mediaItems
        .filter(item =>
            item.mediaType === MediaType.Image ||
            item.mediaType === MediaType.Video ||
            item.mediaType === MediaType.Audio
        )
        .map(item => item);
}


export function getAppDetails(workspace: Workspace, isMultipleAppOrganisation: boolean){    
    return { 
        id:workspace.id,
        name: workspace.name,
        iconUrl: workspace.icon.url,
        appGuid: workspace.appGuid,
        isMultipleAppOrganisation
    }
}

export function getParentListDetails(parentList: ParentList | undefined, workspace: Workspace){
    if (parentList?.id) {
        return { 
            id: parentList.id,
            title: parentList.title,
            link: getContentUrl(
                workspace.appGuid, 
                parentList.id.toString()
            )
        };
    }
    return undefined; 
}


export function getActiveParentList(parentLists: ParentList | ParentList[]): ParentList | undefined {
    const listArray = Array.isArray(parentLists) ? parentLists : [parentLists];

    for (const parentList of listArray) {
        if (parentList?.id) {
            return parentList;
        }
    }

    return undefined;
}


export function getUniqueActivityKey(type: string, activityId: number,  createdAt: string, authorId?:number,) {
   if (authorId) {
    return `${type}-${activityId.toString()}-${authorId.toString()}-${createdAt}`

   }
    return `${type}-${activityId.toString()}-unknown-user-${createdAt}`
}

// unique activity key would require more branch logic than would be ideal for the timestamp stuffs, so adding a simpler
// unique id for that purpose
export function getCompositeId(type: string, authorId: string, createdAt: string) {
    const  activityUnixTimeStamp = new Date(createdAt).getTime();
    return `${type}-${authorId ?? 'unknown'}-${activityUnixTimeStamp}`.toLowerCase();
}

export function getAcknowledgementType(type: string): AcknowledgementType {
    switch (type) {
        case "compliancePost":
            return AcknowledgementType.compliancePost;
        case "eventPost":
            return AcknowledgementType.eventPost;
        case "competitionPost":
            return AcknowledgementType.competitionPost;
        default:
            return AcknowledgementType.compliancePost;
    }
}

export function getContentDeletedBannerMessage(needsApproval?: boolean, isModeratedContent?: boolean, isArchived?: boolean, contentTitle?: string,): string {
    const deletionStatus =
        isArchived
            ?   "Archived"
            :   `Removed from ${contentTitle ?? "list"}`;
        
    const approvalStatus = 
        needsApproval  
            ?   "Not Approved"
            :   "Approved";
    return isModeratedContent
        ?   `Content ${approvalStatus} & ${deletionStatus}`
        :   `Content ${deletionStatus}`
}

export function getCommentArchivalBannerMessage(isArchived?: boolean): string {
    return isArchived
        ?   "Content Archived" 
        :   "Comment Deleted"
}